<template>
  <admin-wrapper
    ref="send-message-wrapper"
    :module-enabler="moduleEnabler"
    :straight-title="additionalModuleName"
    :inputs="inputs"
    :has-buttons="isSendEmailCopyEnabled || isSendSmsCopyEnabled"
    :is-custom-saving="isSaving"
    has-validation
    @save="save"
  >
    <template v-for="input in inputs" #[`${input.name}.input`]>
      <v-col cols="8" :key="input.name">
        <data-input v-model="params[input.name]" v-bind="input" v-on="input.listeners" />
      </v-col>
    </template>
    <template #[`messageConfiguration.input`]>
      <v-row no-gutters>
        <v-col cols="6">
          <send-message-configuration />
        </v-col>
      </v-row>
    </template>
  </admin-wrapper>
</template>

<script>
import { get, call } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';
import SendMessageConfiguration from '@/components/SendMessageConfiguration';

export default {
  name: 'SendMessageSettings',
  components: {
    AdminWrapper,
    DataInput,
    SendMessageConfiguration,
  },
  data: () => ({
    params: {
      send_message_email_cc_enabled: false,
      send_message_email_addresses: [],
      send_message_sms_cc_enabled: false,
      send_message_sms_phone_numbers: [],
    },
    isSaving: false,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules@SEND_MESSAGE_MODULE.verbose_name'),
    current_send_message_email_cc_enabled: get('admin/settingsValues@send_message_email_cc_enabled'),
    current_send_message_sms_cc_enabled: get('admin/settingsValues@send_message_sms_cc_enabled'),
    current_send_message_email_addresses: get('admin/settingsValues@send_message_email_addresses'),
    current_send_message_sms_phone_numbers: get('admin/settingsValues@send_message_sms_phone_numbers'),
    isSendSmsCopyEnabled: get('admin/settingsValues@send_message_sms_superadmin_cc_enabled'),
    isSendEmailCopyEnabled: get('admin/settingsValues@send_message_email_superadmin_cc_enabled'),
    isSendEmailSmsConfigurationEnabled: get('admin/settingsValues@send_message_templates_superadmin_enabled'),
    moduleEnabler() {
      return {
        name: 'send_message_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      const defaultcomboboxParams = {
        dataType: { name: 'combobox' },
        align: 'start',
        appendIcon: '',
        multiple: true,
        chips: true,
        deletableChips: true,
        outlined: true,
        dense: true,
        listeners: {
          drop: e => this.handleNoDrop(e),
          dragend: e => this.handleNoDrop(e),
          dragenter: e => this.handleNoDrop(e),
        },
      };
      return [
        ...(this.isSendEmailCopyEnabled
          ? [
              {
                name: 'send_message_email_cc_enabled',
                translationPath: 'admin.sendMessage.sendEmailCopies',
                dataType: { name: 'switch' },
                align: 'start',
              },
              {
                ...defaultcomboboxParams,
                name: 'send_message_email_addresses',
                translationPath: 'admin.sendMessage.emails',
                subtitle: 'admin.sendMessage.emailsSubtitle',
                disabled: !this.params['send_message_email_cc_enabled'],
              },
            ]
          : []),
        ...(this.isSendSmsCopyEnabled
          ? [
              {
                name: 'send_message_sms_cc_enabled',
                translationPath: 'admin.sendMessage.sendSMSCopies',
                dataType: { name: 'switch' },
                align: 'start',
              },
              {
                ...defaultcomboboxParams,
                name: 'send_message_sms_phone_numbers',
                translationPath: 'admin.sendMessage.phones',
                subtitle: 'admin.sendMessage.phonesSubtitle',
                disabled: !this.params['send_message_sms_cc_enabled'],
                rules: [v => (v ? v?.every(tx => !this.$_validatePhone(tx)) : true) || 'rules.invalidPhoneNumbers'],
              },
            ]
          : []),
        ...(this.isSendEmailSmsConfigurationEnabled
          ? [
              {
                name: 'messageConfiguration',
                isNamedSlot: false,
              },
            ]
          : []),
      ];
    },
  },
  methods: {
    getDataSources: call('layers/getLayersMetadata'),
    getLayers: call('layers/getLayers'),
    editSingleSetting: call('admin/editSingleSetting'),
    getConfigurationList: call('sendMessage/getConfigurationList'),
    handleNoDrop(e) {
      e.preventDefault();
      return false;
    },
    async init() {
      this.inputs.forEach(input => {
        this.params[input.name] = this[`current_${input.name}`];
      });
      await Promise.all([
        this.getDataSources({ with_attributes_schema: true }),
        this.getLayers(),
        this.getConfigurationList(),
      ]);
      this.isFetched = true;
    },
    async save() {
      this.isSaving = true;
      try {
        await Promise.all(
          this.inputs.map(input => {
            if (this.params[input.name] === this[`current_${input.name}`]) return;
            return this.editSingleSetting({
              key: input.name,
              body: { value: this.params[input.name] },
            });
          })
        );
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch (error) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.settingChangeFailed'),
        });
        console.log(error);
      } finally {
        this.isSaving = false;
        this.init();
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .v-autocomplete:not(.v-input--is-focused).v-select--chips input {
    max-height: unset !important;
  }
  .v-select__selections {
    flex-wrap: wrap !important;
    gap: 4px;
    .v-chip {
      margin: 0 !important;
    }
  }
}
</style>
